/**
 * URL parameter utilities for Platform Usage page.
 * Pure functions for serialization/deserialization of filter states to/from URL parameters.
 */

import { Grouping as H2OGPTeGrouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/h2ogpte/v1/h2ogpte_pb';
import { Granularity, Grouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { ResourceType } from '../constants/resource';
import { DateRangeType } from '../types';

// URL Parameter Names
export const URL_PARAMS = {
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  GROUP_BY: 'groupBy',
  RESOURCE: 'resource',
  GRANULARITY: 'granularity',
} as const;

// ============================================
// Date Parsing/Formatting
// ============================================

/**
 * Format a Date to ISO date string (YYYY-MM-DD)
 */
export function formatDateParam(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Parse ISO date string to Date object
 * Returns undefined if invalid
 */
export function parseDateParam(dateStr: string | null): Date | undefined {
  if (!dateStr) return undefined;

  // Validate format YYYY-MM-DD
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) return undefined;

  const date = new Date(`${dateStr}T00:00:00`);
  if (isNaN(date.getTime())) return undefined;

  return date;
}

// ============================================
// Grouping Parsing
// ============================================

// Map URL string values to AI Units Grouping enum
const groupByUrlToEnum: Record<string, Grouping> = {
  workspace: Grouping.WORKSPACE,
  user: Grouping.USER,
  service: Grouping.SERVICE,
  component: Grouping.SERVICE, // Backwards compatibility for old bookmarked URLs
  tag: Grouping.TAG,
};

// Map AI Units Grouping enum to URL string values
const groupByEnumToUrl: Partial<Record<Grouping, string>> = {
  [Grouping.WORKSPACE]: 'workspace',
  [Grouping.USER]: 'user',
  [Grouping.SERVICE]: 'service',
  [Grouping.TAG]: 'tag',
  [Grouping.GROUPING_UNSPECIFIED]: 'workspace',
};

// Map URL string values to H2OGPTe Grouping enum
const h2ogpteGroupByUrlToEnum: Record<string, H2OGPTeGrouping> = {
  workspace: H2OGPTeGrouping.WORKSPACE,
  user: H2OGPTeGrouping.USER,
  model: H2OGPTeGrouping.MODEL,
};

// Map H2OGPTe Grouping enum to URL string values
const h2ogpteGroupByEnumToUrl: Partial<Record<H2OGPTeGrouping, string>> = {
  [H2OGPTeGrouping.WORKSPACE]: 'workspace',
  [H2OGPTeGrouping.USER]: 'user',
  [H2OGPTeGrouping.MODEL]: 'model',
  [H2OGPTeGrouping.GROUPING_UNSPECIFIED]: 'workspace',
};

/**
 * Parse groupBy URL param to AI Units Grouping enum
 */
export function parseGroupByParam(value: string | null): Grouping | undefined {
  if (!value) return undefined;
  return groupByUrlToEnum[value.toLowerCase()];
}

/**
 * Format AI Units Grouping enum to URL param
 */
export function formatGroupByParam(grouping: Grouping): string {
  return groupByEnumToUrl[grouping] || 'workspace';
}

/**
 * Parse groupBy URL param to H2OGPTe Grouping enum
 */
export function parseH2OGPTeGroupByParam(value: string | null): H2OGPTeGrouping | undefined {
  if (!value) return undefined;
  return h2ogpteGroupByUrlToEnum[value.toLowerCase()];
}

/**
 * Format H2OGPTe Grouping enum to URL param
 */
export function formatH2OGPTeGroupByParam(grouping: H2OGPTeGrouping): string {
  return h2ogpteGroupByEnumToUrl[grouping] || 'workspace';
}

// ============================================
// Resource Type Parsing
// ============================================

/**
 * Parse resource URL param to ResourceType enum
 */
export function parseResourceParam(value: string | null): ResourceType | undefined {
  if (!value) return undefined;
  if (value === ResourceType.AI_UNITS || value === ResourceType.H2OGPTE_TOKENS) {
    return value as ResourceType;
  }
  return undefined;
}

/**
 * Format ResourceType enum to URL param
 */
export function formatResourceParam(resource: ResourceType): string {
  return resource;
}

// ============================================
// Granularity Parsing
// ============================================

// Map URL string values to Granularity enum
const granularityUrlToEnum: Record<string, Granularity> = {
  hourly: Granularity.HOUR,
  daily: Granularity.DAY,
  weekly: Granularity.WEEK,
  monthly: Granularity.MONTH,
  yearly: Granularity.YEAR,
};

// Map Granularity enum to URL string values
const granularityEnumToUrl: Partial<Record<Granularity, string>> = {
  [Granularity.HOUR]: 'hourly',
  [Granularity.DAY]: 'daily',
  [Granularity.WEEK]: 'weekly',
  [Granularity.MONTH]: 'monthly',
  [Granularity.YEAR]: 'yearly',
  [Granularity.GRANULARITY_UNSPECIFIED]: 'daily',
};

/**
 * Parse granularity URL param to Granularity enum
 */
export function parseGranularityParam(value: string | null): Granularity | undefined {
  if (!value) return undefined;
  return granularityUrlToEnum[value.toLowerCase()];
}

/**
 * Format Granularity enum to URL param
 */
export function formatGranularityParam(granularity: Granularity): string {
  return granularityEnumToUrl[granularity] || 'daily';
}

// ============================================
// URL State Interface
// ============================================

export interface PlatformUsageUrlState {
  startDate?: Date;
  endDate?: Date;
  groupBy?: Grouping;
  h2ogpteGroupBy?: H2OGPTeGrouping;
  resource?: ResourceType;
  granularity?: Granularity;
}

/**
 * Parse all URL params into state object
 */
export function parseUrlParams(searchParams: URLSearchParams): PlatformUsageUrlState {
  const resource = parseResourceParam(searchParams.get(URL_PARAMS.RESOURCE));
  const isH2OGPTe = resource === ResourceType.H2OGPTE_TOKENS;
  const groupByValue = searchParams.get(URL_PARAMS.GROUP_BY);

  return {
    startDate: parseDateParam(searchParams.get(URL_PARAMS.START_DATE)),
    endDate: parseDateParam(searchParams.get(URL_PARAMS.END_DATE)),
    groupBy: isH2OGPTe ? undefined : parseGroupByParam(groupByValue),
    h2ogpteGroupBy: isH2OGPTe ? parseH2OGPTeGroupByParam(groupByValue) : undefined,
    resource,
    granularity: parseGranularityParam(searchParams.get(URL_PARAMS.GRANULARITY)),
  };
}

/**
 * Build URL search params from state
 * Only includes non-default values to keep URLs clean
 */
export function buildUrlParams(
  state: {
    startDate?: Date;
    endDate?: Date;
    groupBy?: Grouping;
    h2ogpteGroupBy?: H2OGPTeGrouping;
    resource?: ResourceType;
    dateRangeType?: DateRangeType;
    granularity?: Granularity;
  },
  defaults: {
    defaultGroupBy: Grouping;
    defaultH2OGPTeGroupBy: H2OGPTeGrouping;
    defaultResource: ResourceType;
    defaultGranularity: Granularity;
  }
): URLSearchParams {
  const params = new URLSearchParams();

  // Resource type - only add if not default
  if (state.resource && state.resource !== defaults.defaultResource) {
    params.set(URL_PARAMS.RESOURCE, formatResourceParam(state.resource));
  }

  // GroupBy - only add if not default for the resource type
  const isH2OGPTe = state.resource === ResourceType.H2OGPTE_TOKENS;
  if (isH2OGPTe && state.h2ogpteGroupBy !== undefined && state.h2ogpteGroupBy !== defaults.defaultH2OGPTeGroupBy) {
    params.set(URL_PARAMS.GROUP_BY, formatH2OGPTeGroupByParam(state.h2ogpteGroupBy));
  } else if (!isH2OGPTe && state.groupBy !== undefined && state.groupBy !== defaults.defaultGroupBy) {
    params.set(URL_PARAMS.GROUP_BY, formatGroupByParam(state.groupBy));
  }

  // Date range - always included
  if (state.startDate) {
    params.set(URL_PARAMS.START_DATE, formatDateParam(state.startDate));
  }
  if (state.endDate) {
    params.set(URL_PARAMS.END_DATE, formatDateParam(state.endDate));
  }
  // Granularity - always included
  if (state.granularity !== undefined) {
    params.set(URL_PARAMS.GRANULARITY, formatGranularityParam(state.granularity));
  }

  return params;
}
